/**
 * Блок «Показатели социально-экономического развития» районной вкладки
 * (US-11 FR-5, выдача — US-12 FR-8).
 *
 * Отдельный компонент, а не режим областного `SerTable`: у районного
 * блока другой состав отраслей (промышленность · сельское хозяйство ·
 * инвестиции · строительство вместо обрабатывающей промышленности),
 * другая глубина перехода и объём, уже приведённый к млрд ₸ платформой.
 *
 * Правила, которые задают вёрстку:
 *  - **Четыре отрасли видны всегда** и в порядке ответа (FR-5.1, FR-8.3):
 *    отрасль без данных — строка без значений, а не пропуск. Ноль вместо
 *    прочерка означал бы, что работы велись в нулевом объёме (FR-5.5).
 *  - **Объём — в единице ответа** (US-12 FR-3.1): источники ведут
 *    промышленность и инвестиции в тыс. ₸, сельское хозяйство в млн, а
 *    строительство в целых ₸; приведение делает платформа, и экран
 *    печатает величину с объявленной единицей, ничего не пересчитывая.
 *  - **ИФО приходит как есть** (110,6 %, а не 10,6 %), а рядом экран
 *    показывает динамику к 100 % со знаком и стрелкой: «+10,6 %»
 *    (FR-5.4). Сам индекс при этом не подменяется.
 *  - **Период свой у каждой отрасли** (FR-8.5): публикации выходят в
 *    разные дни месяца, и одна подпись на блок отнесла бы часть отраслей
 *    к чужой дате. Общий период у блока в ответе отсутствует намеренно.
 *  - **Клик — на страницу отрасли в «Аналитике»** (FR-5.6): это и есть
 *    целевая глубина drill-through. Строка кликабельна только при
 *    `drill_through_available`; у промышленности своей страницы нет, и
 *    строка остаётся неактивной — без вида кнопки.
 */
import type { LucideIcon } from 'lucide-react';
import {
  Building2,
  ChevronRight,
  Coins,
  Factory,
  TrendingDown,
  TrendingUp,
  Wheat,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { BlockLoader } from '../../../../components/common/BlockLoader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import { cn } from '../../../../lib/cn';
import { ANALYTICS_SECTION_MODULE } from '../../../analytics/analytics-visibility';
import type { AnalyticsFilter } from '../../../analytics/analytics.page';
import type {
  DistrictSerIndustry,
  DistrictSerResponse,
} from '../../../../types/district-passport';

/** Иконки отраслей по ключу ответа; неизвестная отрасль идёт без иконки. */
const ICONS: Record<string, LucideIcon> = {
  industry: Factory,
  agriculture: Wheat,
  investment: Coins,
  construction: Building2,
};

/**
 * `analytics_section` ответа — это `module_id` раздела «Аналитики», а чип
 * страницы адресуется её фильтром. Карта строится обращением реестра
 * видимости, чтобы имена разделов жили в одном месте.
 */
const SECTION_BY_MODULE: Record<string, AnalyticsFilter> = Object.fromEntries(
  Object.entries(ANALYTICS_SECTION_MODULE).map(([filter, moduleId]) => [
    moduleId,
    filter as AnalyticsFilter,
  ]),
);

const fmt1 = (v: number): string =>
  Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v);

/**
 * Объём — до двух знаков: выдача округляет млрд ₸ до трёх (4,073), и один
 * знак на районных величинах терял бы реальную точность — 4,07 млрд ₸
 * превращались бы в 4,1, а по такой цифре сверяют с бюллетенем.
 */
const fmtVolume = (v: number): string =>
  Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(v);

/** Динамика к 100 % со знаком: ИФО 110,6 % → «+10,6 %» (FR-5.4). */
const signedChange = (ifo: number): string => {
  const delta = ifo - 100;
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
  return `${sign}${fmt1(Math.abs(delta))}%`;
};

interface Props {
  data: DistrictSerResponse | null;
  loading: boolean;
}

export const DistrictSer = ({ data, loading }: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isKz = i18n.language === 'kz';

  const industries = data?.industries ?? [];

  /** Раздел «Аналитики», в который ведёт строка; null — перехода нет. */
  const sectionOf = (row: DistrictSerIndustry): AnalyticsFilter | null => {
    if (!row.drill_through_available || row.analytics_section === null) {
      return null;
    }
    return SECTION_BY_MODULE[row.analytics_section] ?? null;
  };

  const open = (section: AnalyticsFilter) => {
    navigate(`/analytics?section=${section}`);
  };

  return (
    <Card className="shadow-card" data-testid="district-ser">
      <CardHeader className="flex-row flex-wrap items-baseline justify-between gap-x-4 gap-y-1 space-y-0">
        <CardTitle>{t('digitalPassport.districtSer.title')}</CardTitle>
        {/* Накопительность — свойство всех четырёх величин (FR-5.3). */}
        {industries.some((row) => row.cumulative) && (
          <span className="min-w-0 shrink text-sm text-text-faint">
            {t('digitalPassport.districtSer.cumulative')}
          </span>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <BlockLoader />
        ) : industries.length === 0 ? (
          <p
            data-testid="block-placeholder"
            className="text-sm lowercase text-text-muted"
          >
            {t('common.noData')}
          </p>
        ) : (
          <section data-testid="block-district-ser">
            <div className="flex items-center gap-2 border-b border-surface-border pb-2 text-sm text-text-faint">
              <span className="min-w-0 flex-1" />
              <span className="w-28 shrink-0 text-right">
                {t('digitalPassport.districtSer.columnVolume')}
              </span>
              <span className="w-20 shrink-0 text-right">
                {t('digitalPassport.districtSer.columnIfo')}
              </span>
              <span className="w-24 shrink-0 text-right">
                {t('digitalPassport.districtSer.columnChange')}
              </span>
            </div>

            <ul>
              {industries.map((row, index) => {
                const key = row.key ?? `row-${index}`;
                const Icon = row.key !== null ? ICONS[row.key] : undefined;
                const name = (isKz ? row.name_kz : row.name_ru) ?? row.name_ru;
                const section = sectionOf(row);
                const periodLabel =
                  (isKz ? row.period?.label_kz : row.period?.label_ru) ?? null;
                const ifo = row.ifo_percent;

                return (
                  <li
                    key={key}
                    data-testid={`district-ser-row-${key}`}
                    className="border-b border-surface-border last:border-b-0"
                  >
                    <div
                      className={cn(
                        'flex items-center gap-2 py-3 text-sm',
                        section !== null &&
                          'cursor-pointer rounded-md hover:bg-brand-50/60',
                      )}
                      {...(section !== null
                        ? {
                            role: 'button',
                            tabIndex: 0,
                            'data-testid': `district-ser-row-${key}-link`,
                            title: t('digitalPassport.districtSer.drillHint'),
                            onClick: () => open(section),
                            onKeyDown: (event: React.KeyboardEvent) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                open(section);
                              }
                            },
                          }
                        : {
                            // Страницы отрасли нет — строка неактивна и
                            // видом кнопки не притворяется (FR-5.6).
                            'aria-disabled': true,
                          })}
                    >
                      <span className="flex min-w-0 flex-1 items-center gap-3">
                        {section !== null ? (
                          <ChevronRight className="size-4 shrink-0 text-brand-600" />
                        ) : (
                          <span className="w-4 shrink-0" aria-hidden="true" />
                        )}
                        {Icon !== undefined && (
                          <Icon
                            strokeWidth={1.7}
                            className="size-6 shrink-0 text-brand-500"
                            aria-hidden="true"
                          />
                        )}
                        <span className="min-w-0">
                          <span className="block break-words font-semibold leading-snug">
                            {name ?? '—'}
                          </span>
                          {/* Период отрасли — свой (FR-8.5); отставание
                              выпуска подписано, а не скрыто (FR-7.3). */}
                          {periodLabel !== null && (
                            <span
                              className="block text-xs text-text-faint"
                              data-testid={`district-ser-row-${key}-period`}
                            >
                              {periodLabel}
                              {row.period?.is_current === false &&
                                ` · ${t('digitalPassport.districtSer.lastAvailable')}`}
                            </span>
                          )}
                        </span>
                      </span>

                      <span
                        data-testid={`district-ser-row-${key}-volume`}
                        className="w-28 shrink-0 text-right tabular-nums"
                      >
                        {row.volume !== null ? (
                          <>
                            <b className="font-extrabold">
                              {fmtVolume(row.volume)}
                            </b>
                            {(isKz ? row.volume_unit_kz : row.volume_unit_ru) !=
                              null && (
                              <span className="ml-1 text-xs font-normal text-text-muted">
                                {isKz ? row.volume_unit_kz : row.volume_unit_ru}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-sm font-normal text-text-faint">
                            {t('common.noData')}
                          </span>
                        )}
                      </span>

                      {/* ИФО печатается как есть, в процентах (FR-8.3). */}
                      <span
                        data-testid={`district-ser-row-${key}-ifo`}
                        className="w-20 shrink-0 text-right font-bold tabular-nums text-chart-indigo"
                      >
                        {ifo !== null ? `${fmt1(ifo)}%` : '—'}
                      </span>

                      <span
                        data-testid={`district-ser-row-${key}-change`}
                        className={cn(
                          'flex w-24 shrink-0 items-center justify-end gap-1 font-bold tabular-nums',
                          ifo !== null && ifo < 100
                            ? 'text-status-danger'
                            : 'text-brand-600',
                        )}
                      >
                        {ifo !== null ? (
                          <>
                            {ifo < 100 ? (
                              <TrendingDown
                                className="size-4 shrink-0"
                                aria-hidden="true"
                              />
                            ) : (
                              <TrendingUp
                                className="size-4 shrink-0"
                                aria-hidden="true"
                              />
                            )}
                            {signedChange(ifo)}
                          </>
                        ) : (
                          '—'
                        )}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* База сравнения ИФО — готовой подписью из ответа (FR-5.2). */}
            {industries[0] !== undefined &&
              (isKz
                ? industries[0].ifo_basis_kz
                : industries[0].ifo_basis_ru) != null && (
                <p className="mt-3 text-xs leading-snug text-text-faint">
                  {t('digitalPassport.districtSer.columnIfo')}:{' '}
                  {isKz
                    ? industries[0].ifo_basis_kz
                    : industries[0].ifo_basis_ru}
                </p>
              )}
          </section>
        )}
      </CardContent>
    </Card>
  );
};
